import { z } from 'zod';
import { domainValue, firmTypologyValue, subscriptionPaymentMethodValue, subscriptionPlanValue, } from './value-objects/subscription-enums';
const catalogProductPricesSchema = z.object({
    annual: z.string().trim().min(1).nullable(),
    daily: z.string().trim().min(1).nullable(),
    monthly: z.string().trim().min(1).nullable(),
});
const catalogFirmTypologyValue = z.preprocess(value => (value === 'classic' ? 'small' : value), firmTypologyValue);
const catalogProductSchema = z.object({
    billingPortalConfigurationId: z.string().trim().min(1).nullable(),
    domain: domainValue,
    firmTypology: catalogFirmTypologyValue,
    plan: subscriptionPlanValue,
    planId: z.string(),
    prices: catalogProductPricesSchema,
    stripeProductId: z.string(),
    subscriptionPaymentMethod: subscriptionPaymentMethodValue,
});
const catalogFileSchema = z.object({
    products: z.array(catalogProductSchema).min(1),
});
const BILLING_PERIOD_KEYS = ['daily', 'monthly', 'annual'];
export function buildStripeProductCatalogIndex(catalog) {
    const allowedProductIds = new Set();
    const allowedLookupKeys = new Set();
    const allowedPriceIds = new Set();
    const resolvedByLookupKey = new Map();
    const resolvedByPriceId = new Map();
    for (const product of catalog.products) {
        allowedProductIds.add(product.stripeProductId);
        for (const billingPeriod of BILLING_PERIOD_KEYS) {
            const stripePriceLookupKey = product.prices[billingPeriod];
            if (!stripePriceLookupKey) {
                continue;
            }
            if (resolvedByLookupKey.has(stripePriceLookupKey)) {
                throw new Error(`Duplicate stripe price lookup key: ${stripePriceLookupKey}`);
            }
            allowedLookupKeys.add(stripePriceLookupKey);
            const resolvedEntry = {
                billingPeriod,
                billingPortalConfigurationId: product.billingPortalConfigurationId,
                domain: product.domain,
                firmTypology: product.firmTypology,
                plan: product.plan,
                planId: product.planId,
                stripePriceLookupKey,
                stripeProductId: product.stripeProductId,
                subscriptionPaymentMethod: product.subscriptionPaymentMethod,
            };
            resolvedByLookupKey.set(stripePriceLookupKey, resolvedEntry);
            if (resolvedEntry.stripePriceId) {
                allowedPriceIds.add(resolvedEntry.stripePriceId);
                resolvedByPriceId.set(resolvedEntry.stripePriceId, resolvedEntry);
            }
        }
    }
    return {
        allowedLookupKeys,
        allowedPriceIds,
        allowedProductIds,
        resolvedByLookupKey,
        resolvedByPriceId,
    };
}
export function enrichStripeProductCatalogIndex(catalog, stripePriceIdsByLookupKey) {
    const allowedPriceIds = new Set();
    const resolvedByLookupKey = new Map();
    const resolvedByPriceId = new Map();
    for (const lookupKey of catalog.allowedLookupKeys) {
        const entry = catalog.resolvedByLookupKey.get(lookupKey);
        if (!entry) {
            continue;
        }
        const stripePriceId = stripePriceIdsByLookupKey.get(lookupKey);
        if (!stripePriceId) {
            continue;
        }
        const enrichedEntry = {
            ...entry,
            stripePriceId,
        };
        resolvedByLookupKey.set(lookupKey, enrichedEntry);
        allowedPriceIds.add(stripePriceId);
        resolvedByPriceId.set(stripePriceId, enrichedEntry);
    }
    return {
        ...catalog,
        allowedLookupKeys: new Set(resolvedByLookupKey.keys()),
        allowedPriceIds,
        resolvedByLookupKey,
        resolvedByPriceId,
    };
}
export function listStripeProductCatalogVariants(catalog) {
    const billingPeriodOrder = { annual: 2, daily: 0, monthly: 1 };
    const domainOrder = {
        business_law: 1,
        formations: 2,
        labor_law: 0,
    };
    const firmTypologyOrder = { large: 2, medium: 1, small: 0 };
    const planOrder = {
        ccn: 5,
        dokia_conseil: 3,
        dokia_formations: 4,
        dokia_solution: 2,
        societe: 0,
        'societe+': 1,
    };
    const catalogIndex = buildStripeProductCatalogIndex(catalog);
    return [...catalogIndex.resolvedByLookupKey.values()]
        .sort((left, right) => {
        const billingPeriodComparison = billingPeriodOrder[left.billingPeriod] -
            billingPeriodOrder[right.billingPeriod];
        if (billingPeriodComparison !== 0) {
            return billingPeriodComparison;
        }
        const domainComparison = domainOrder[left.domain] - domainOrder[right.domain];
        if (domainComparison !== 0) {
            return domainComparison;
        }
        const planComparison = planOrder[left.plan] - planOrder[right.plan];
        if (planComparison !== 0) {
            return planComparison;
        }
        return (firmTypologyOrder[left.firmTypology] -
            firmTypologyOrder[right.firmTypology]);
    });
}
export function parseStripeProductCatalogFile(input) {
    return catalogFileSchema.parse(input);
}
export function resolveBillingPortalConfigurationId(catalog, stripePriceLookupKey) {
    const billingPortalConfigurationId = catalog.resolvedByLookupKey
        .get(stripePriceLookupKey)
        ?.billingPortalConfigurationId;
    return billingPortalConfigurationId ?? undefined;
}
//# sourceMappingURL=stripe-product-catalog.js.map